// Where to send a user after login. ProtectedRoute stashes the path it turned
// away in the redirect state, and LoginPage resolves it through here (sc-6158) —
// a Slack deep link clicked hours later usually hits a cold session, and landing
// on the default page means going back to hunting for the record by hand.
//
// Everything here is about not trusting that stashed value. It arrives via
// router state, which a crafted link can populate, so an unchecked `from` is an
// open redirect that fires on an admin the instant they authenticate.
//
// DEPENDENCY, and breaking it is silent: the mechanism rides on router state
// surviving login, which it only does because Edge Admin signs in through an
// in-page Google credential callback (@react-oauth/google, in LoginPage) rather
// than a full-page bounce to the provider. Move auth to a redirect flow — an OAuth
// code flow, a hosted login page, an SSO bounce — and `location.state` is gone by
// the time the app loads again. Nothing throws: the redirect still "works" and
// every user simply lands on '/', which nobody reports as a bug. If that migration
// happens, park the path somewhere durable (sessionStorage) before the bounce and
// read it back here instead.
const DEFAULT_PATH = '/';

export function safeRedirectPath(from: unknown, fallback: string = DEFAULT_PATH): string {
  if (typeof from !== 'string' || from.length === 0) return fallback;

  // Same-origin absolute paths only.
  if (!from.startsWith('/')) return fallback;

  // "//evil.com" is protocol-relative, and browsers normalize "/\evil.com" to the
  // same thing — both leave the app despite starting with a slash.
  if (from[1] === '/' || from[1] === '\\') return fallback;

  // Bouncing back to the login page would loop.
  if (from === '/login' || /^\/login[/?#]/.test(from)) return fallback;

  return from;
}
